/**
 * M2 P3: AI traffic. Pure, deterministic, engine-agnostic (no Three.js, no
 * Math.random) so NPC motion is reproducible and could run server-side later.
 *
 * An NpcShip flies a lane between two stations using arrive+seek steering (cruise
 * speed, linear slow-down near the destination). The client owns world-position
 * resolution (stations sit at host-body + offset) and feeds resolved target
 * positions into stepNpc; sim-core only does the math. makeTraffic spawns a
 * deterministic fleet from a seed; nextLeg flips the route on arrival so traffic
 * loops between stations forever.
 */
import { add, sub, scale, length, normalize, vec3, type Vec3 } from './vec3.js';
import { identity, type Quat } from './quat.js';
import type { StationSpec } from './station.js';
import type { FactionId } from './faction.js';
import { ambientTrafficTypesForFaction } from './ship.js';

/** Default lane-traffic hull pool (faction-agnostic, civilian). */
const DEFAULT_TRAFFIC_TYPES = ['fighter', 'hauler', 'heavy'] as const;

/** Resolve the traffic hull pool: a faction's ambient civilian pool, or the
 * default lane fleet when no faction is requested (legacy behaviour). */
const trafficPool = (faction?: FactionId): readonly string[] =>
  faction ? ambientTrafficTypesForFaction(faction) : DEFAULT_TRAFFIC_TYPES;

export type NpcPhase = 'CRUISING' | 'ARRIVING';

export interface NpcShip {
  id: string; // 'npc-0', 'npc-1', ...
  shipType: string; // 'fighter' | 'hauler' | 'heavy' (GLB selection)
  pos: Vec3; // Float64 authoritative world position (km)
  vel: Vec3; // km/s
  /** 6DOF orientation for physics-driven combat ships; identity for kinematic traffic. */
  orientation: Quat;
  originId: string; // station id this leg departed from
  destId: string; // station id this leg heads to
  laneT: number; // 0..1 spawn fraction along the lane (for client world re-seed)
  phase: NpcPhase;
}

// --- Numeric constants (documented; tunable, ship as-is). ---
/** Steady-state lane speed. */
export const NPC_CRUISE_SPEED_KMS = 8;
/** Hard speed clamp (== SHIP_HEAVY.maxSpeedKmS). */
export const NPC_MAX_SPEED_KMS = 12;
/** Begin linear slow-down inside this distance to the destination. */
export const NPC_ARRIVE_RADIUS_KM = 4_000;
/** Within this of the destination => flip to the next leg. */
export const NPC_ARRIVAL_SNAP_KM = 200;

/** Deterministic integer hash from (seed, i) — replaces Math.random. */
const hash = (seed: number, i: number): number =>
  ((Math.imul(seed >>> 0, 2654435761) ^ Math.imul(i >>> 0, 40503)) >>> 0);

/** Arrive steering: cruise outside the radius, linear ramp to ~0 at the target. */
const arriveSpeed = (dist: number): number => {
  const desired =
    dist >= NPC_ARRIVE_RADIUS_KM
      ? NPC_CRUISE_SPEED_KMS
      : NPC_CRUISE_SPEED_KMS * (dist / NPC_ARRIVE_RADIUS_KM);
  return Math.min(desired, NPC_MAX_SPEED_KMS);
};

/**
 * Pure kinematic arrive+seek. Returns a NEW NpcShip moved toward targetPos by dt.
 * Velocity is set directly to the desired arrive velocity (deterministic).
 */
export const stepNpc = (npc: NpcShip, targetPos: Vec3, dt: number): NpcShip => {
  const toTarget = sub(targetPos, npc.pos);
  const dist = length(toTarget);
  const vel = dist > 0 ? scale(normalize(toTarget), arriveSpeed(dist)) : vec3();
  const pos = add(npc.pos, scale(vel, dt));
  const phase: NpcPhase = dist <= NPC_ARRIVE_RADIUS_KM ? 'ARRIVING' : 'CRUISING';
  return { ...npc, pos, vel, phase }; // orientation unchanged (kinematic)
};

/**
 * Standoff far threshold (km) — hostile closes until within this range.
 * Set inside the 90 km NPC_FIRE_RANGE and 120 km bolt range so a hostile
 * spawning ~80 km out approaches and then jockeys in the engagement band.
 */
export const STANDOFF_FAR_KM = 70;
/**
 * Standoff near threshold (km) — hostile backs off when closer than this.
 * Keeps the ship inside the fire/hit sweet spot and prevents ramming.
 */
export const STANDOFF_NEAR_KM = 30;
/**
 * Pursuit speed for the combat-approach branch (km/s). Much faster than the
 * lane cruise (8 km/s) so a hostile actually closes the gap in a fight.
 * Retreat is half this value.
 */
export const COMBAT_PURSUIT_SPEED_KMS = 50;

/**
 * Pure combat pursuit AI: hostile enc-* ship seeks the player but holds a
 * standoff band between STANDOFF_NEAR_KM and STANDOFF_FAR_KM.
 * - dist > STANDOFF_FAR_KM  → approach at COMBAT_PURSUIT_SPEED_KMS
 * - dist < STANDOFF_NEAR_KM → back away at half pursuit speed
 * - between the bands       → hold / slow drift (zero desired velocity)
 * Deterministic (playerPos is the only external input).
 * Returns a NEW NpcShip with updated pos + vel.
 */
export const stepCombatNpc = (npc: NpcShip, playerPos: Vec3, dt: number): NpcShip => {
  const toPlayer = sub(playerPos, npc.pos);
  const dist = length(toPlayer);
  let vel: Vec3;
  if (dist < 1e-6) {
    vel = vec3();
  } else if (dist > STANDOFF_FAR_KM) {
    vel = scale(normalize(toPlayer), COMBAT_PURSUIT_SPEED_KMS);
  } else if (dist < STANDOFF_NEAR_KM) {
    vel = scale(normalize(toPlayer), -(COMBAT_PURSUIT_SPEED_KMS * 0.5)); // retreat
  } else {
    vel = vec3(); // hold in standoff band
  }
  const pos = add(npc.pos, scale(vel, dt));
  return { ...npc, pos, vel, phase: 'CRUISING' };
};

/**
 * Flip the route on arrival: new origin = old dest, new dest = a different
 * station chosen deterministically from the npc id. Pure. No-op if < 2 stations.
 */
export const nextLeg = (npc: NpcShip, stations: readonly StationSpec[]): NpcShip => {
  if (stations.length < 2) return npc;
  const originIdx = stations.findIndex((s) => s.id === npc.destId);
  const base = originIdx < 0 ? 0 : originIdx;
  const idDigits = Number(npc.id.replace(/\D/g, '')) || 0;
  const step = idDigits % (stations.length - 1);
  const destIdx = (base + 1 + step) % stations.length;
  const newOrigin = stations[base] as StationSpec;
  const newDest = stations[destIdx] as StationSpec;
  return { ...npc, originId: newOrigin.id, destId: newDest.id, phase: 'CRUISING' };
};

/** Lerp two vectors by t in [0,1]. */
const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => add(scale(a, 1 - t), scale(b, t));

/**
 * Deterministic spawner: `count` NPCs on station-to-station lanes, seeded by
 * `seed` (no Math.random). pos is seeded in OFFSET space (the client re-seeds to
 * true world positions via the resolved station lane and the carried laneT).
 */
export const makeTraffic = (
  stations: readonly StationSpec[],
  count: number,
  seed: number,
  faction?: FactionId,
): NpcShip[] => {
  const out: NpcShip[] = [];
  const n = stations.length;
  if (n < 2) return out;
  const types = trafficPool(faction);
  for (let i = 0; i < count; i++) {
    const originIdx = hash(seed, i * 3) % n;
    const destIdx = (originIdx + 1 + (hash(seed, i * 3 + 1) % (n - 1))) % n;
    const origin = stations[originIdx] as StationSpec;
    const dest = stations[destIdx] as StationSpec;
    const laneT = (hash(seed, i * 7) % 1000) / 1000;
    out.push({
      id: `npc-${i}`,
      shipType: types[hash(seed, i * 3 + 2) % types.length] as string,
      pos: lerp(origin.offset, dest.offset, laneT),
      vel: vec3(),
      orientation: identity(),
      originId: origin.id,
      destId: dest.id,
      laneT,
      phase: 'CRUISING',
    });
  }
  return out;
};

/**
 * M3 WORLD B4: busier stations. A dense local cohort of `count` NPCs that loiter
 * NEAR one station (arrivals/departures + a short dock queue) so the port reads as
 * inhabited. Each ship's lane runs the station to ITSELF with a tight offset so it
 * orbits/holds in the neighbourhood instead of crossing the system. Deterministic
 * (seeded, no Math.random). ids are 'sta-<i>' so they stay distinct from the
 * 'npc-' lane traffic and 'enc-' encounter ships. The client re-seeds pos to the
 * resolved station world position (like makeTraffic).
 */
export const makeStationTraffic = (
  station: StationSpec,
  count: number,
  seed: number,
  faction?: FactionId,
): NpcShip[] => {
  const out: NpcShip[] = [];
  const types = trafficPool(faction);
  for (let i = 0; i < count; i++) {
    const laneT = (hash(seed, i * 5 + 1) % 1000) / 1000;
    const jitter = scale(
      {
        x: (hash(seed, i * 5 + 2) % 1000) / 1000 - 0.5,
        y: (hash(seed, i * 5 + 3) % 1000) / 1000 - 0.5,
        z: (hash(seed, i * 5 + 4) % 1000) / 1000 - 0.5,
      },
      400, // tight ~400 km cluster around the port
    );
    out.push({
      id: `sta-${i}`,
      shipType: types[hash(seed, i * 5) % types.length] as string,
      pos: add(station.offset, jitter),
      vel: vec3(),
      orientation: identity(),
      originId: station.id,
      destId: station.id, // loiters at the port (client gives it a hold pattern)
      laneT,
      phase: 'CRUISING',
    });
  }
  return out;
};
